<script setup>
import DataProvider from '@/components/DataProvider';
import { toSectionedProviderItems } from '@/common/practitionerStatus';
import { useSharedLocationRestriction } from '@/composables/useSharedLocationRestriction';
import { ref, nextTick, useAttrs, computed } from 'vue';
import sortBy from 'lodash/sortBy';

const { scopeUserQuery } = useSharedLocationRestriction();

const props = defineProps({
    modelValue: Object,
    valueType: {
        type: String,
        default: 'Practitioner',
    },
    filter: Function,
    autoClear: Boolean,
    label: String,
    minLength: {
        type: Number,
        default: 0,
    },
    clearable: {
        type: Boolean,
        default: true,
    },
    providerOnly: {
        type: Boolean,
        default: false,
    },
    supervisingProviderOnly: {
        type: Boolean,
        default: false,
    },
    includeInactive: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['update:modelValue', 'itemClick', 'valueCleared', 'blur']);
const attrs = useAttrs();
const autocompleteRef = ref(null);
const search = ref(null);
const users = ref([]);
const isLoading = ref(true);

const loadingDisplayValue = computed(() => props.modelValue?.fullName || props.modelValue?.display || null);

const itemTitle = (item) => item?.practitionerResource?.fullName || item?.fullName || item?.display || '';

const autocompleteValue = computed({
    get() {
        if (!props.modelValue) return null;
        const items = allMappedUsers.value;
        if (props.modelValue.resourceType === 'User') {
            return items.find((item) => item.id === props.modelValue.id) ?? (isLoading.value ? props.modelValue : null);
        }
        if (props.modelValue.resourceType === 'Practitioner') {
            return items.find((item) => item.practitioner?.id === props.modelValue.id) ?? (isLoading.value ? props.modelValue : null);
        }
        return null;
    },
    set(item) {
        if (!item) {
            emit('update:modelValue', null);
            emit('valueCleared');
            return;
        }

        let itemVal;
        if (props.valueType === 'Practitioner') {
            itemVal = {
                id: item.practitionerResource?.id,
                resourceType: 'Practitioner',
                fullName: item.practitionerResource?.fullName,
                display: item.practitionerResource?.fullName,
            };
        } else {
            itemVal = item;
        }

        emit('update:modelValue', itemVal);
        emit('itemClick', item);

        if (props.autoClear) {
            nextTick(() => {
                search.value = null;
            });
        }
    },
});

const loadedData = () => {
    if (autocompleteRef.value && attrs.autofocus) {
        autocompleteRef.value.focus();
    }
};

const userFilter = (firstName, search, item) => {
    let user = item.raw;
    // Section sentinels ({ header } / { divider }) carry no practitionerResource;
    // always keep them so the "Inactive" subheader renders with its group.
    if (user?.header || user?.divider) return true;
    let filterOne, filterTwo, filterThree;

    if (user.practitionerResource.firstName) {
        filterOne = user.practitionerResource.firstName.toLowerCase();
        filterTwo = user.practitionerResource.lastName.toLowerCase();
        filterThree = user.practitionerResource.fullName.toLowerCase();
    }
    const filter = firstName.toLowerCase();
    const searchText = search.toLowerCase();

    return filterOne.indexOf(searchText) > -1 || filterTwo.indexOf(searchText) > -1 || filterThree.indexOf(searchText) > -1 || filter.indexOf(searchText) > -1;
};

// Built once per users load — O(1) practitioner lookups instead of scanning the array per user.
const practitionerMap = computed(() => {
    const map = new Map();
    users.value?.forEach((r) => {
        if (r.resourceType === 'Practitioner') map.set(r.id, r);
    });
    return map;
});

// Stable lookup list: all active mapped users, independent of search state.
// Object.create(user) preserves non-enumerable fhirworks fields via prototype chain
// while keeping practitionerResource as an own property — no mutation of the source object.
const allMappedUsers = computed(() => {
    if (!users.value) return [];
    let mapped = users.value
        .filter((i) => i.resourceType === 'User' && i.practitioner)
        .flatMap((user) => {
            const practitioner = practitionerMap.value.get(user.practitioner.id);
            if (!practitioner) return [];
            return [Object.assign(Object.create(user), { practitionerResource: practitioner })];
        });

    if (!props.includeInactive) {
        return mapped.filter((user) => user.active);
    }

    // Same element references, new array — preserves Vuetify hide-selected identity.
    // Grouping into Active/Inactive sections happens in sectionedItems at display time.
    return sortBy(mapped, [(user) => (user.practitionerResource?.fullName || '').toLowerCase()]);
});

// Restrict to users who share at least one Location with the current user when the
// authStore flag is set (#4582). The scoping logic lives in useSharedLocationRestriction —
// shared by every User-query call site so the FHIR `_has` clause stays consistent with
// the Aidbox access policies (#4616) that reject restricted-user queries lacking it.
// scopeUserQuery reads the composable's computeds, so this recomputes reactively.
const dataProviderQuery = computed(() => {
    return { query: scopeUserQuery({ _include: 'practitioner' }), resourceType: 'User' };
});

// Derives from allMappedUsers so items passed to the dropdown share the same object
// references as the getter — Vuetify's hide-selected comparison works correctly.
// Pre-filtering by search text keeps the dropdown DOM small; Vuetify's custom-filter
// still runs on top but finds fewer items, avoiding hidden-element selector collisions in E2E.
const filteredUsers = computed(() => {
    if (props.minLength > 0 && (!search.value || search.value.length <= props.minLength)) {
        return [];
    }

    let result = allMappedUsers.value;

    if (search.value) {
        const searchText = search.value.toLowerCase();
        result = result.filter((user) => {
            const firstName = user.practitionerResource?.firstName?.toLowerCase() || '';
            const lastName = user.practitionerResource?.lastName?.toLowerCase() || '';
            const fullName = user.practitionerResource?.fullName?.toLowerCase() || '';
            return firstName.includes(searchText) || lastName.includes(searchText) || fullName.includes(searchText);
        });
    }

    if (props.filter) {
        result = result.filter(props.filter);
    }

    if (props.providerOnly) {
        result = result.filter((user) => user?.isProvider === true);
    }

    if (props.supervisingProviderOnly) {
        result = result.filter((user) => user?.isSupervisingProvider === true);
    }

    return result;
});

// Items bound to the autocomplete. When inactive users are included, split them
// into Active (first) and an "Inactive" section — matching the provider pickers
// and the dashboard workboard selector. Otherwise the flat active list is used.
const sectionedItems = computed(() => {
    if (!props.includeInactive) return filteredUsers.value;
    const active = filteredUsers.value.filter((user) => user.active);
    const inactive = filteredUsers.value.filter((user) => !user.active);
    if (!inactive.length) return active;
    return toSectionedProviderItems({ active, inactive });
});
</script>

<template>
    <DataProvider v-model="users" v-model:loading="isLoading" collection :query="dataProviderQuery" @loaded="loadedData">
        <template #loading>
            <v-autocomplete
                :model-value="loadingDisplayValue"
                :items="loadingDisplayValue ? [loadingDisplayValue] : []"
                :disabled="true"
                loading
                v-bind="attrs"
                :label="props.label || 'Search user'"
            />
        </template>
        <template #error> Error </template>
        <template #default>
            <v-autocomplete
                ref="autocompleteRef"
                v-model="autocompleteValue"
                v-model:search="search"
                return-object
                :clearable="props.clearable"
                hide-selected
                :custom-filter="userFilter"
                :items="sectionedItems"
                :item-title="itemTitle"
                :label="props.label || 'Search user'"
                v-bind="attrs"
                persistent
                @click:clear="autocompleteValue = null"
                @blur="$emit('blur')"
            >
                <template #no-data>
                    <v-list-item>
                        <v-list-item-title v-if="props.minLength <= 0"> Users not found </v-list-item-title>
                        <v-list-item-title v-else> Start typing to search for users </v-list-item-title>
                    </v-list-item>
                </template>
                <template #item="{ item, props: itemProps }">
                    <v-list-subheader v-if="item.raw.header" class="bn-section-header">{{ item.raw.header }}</v-list-subheader>
                    <v-divider v-else-if="item.raw.divider" />
                    <v-list-item v-else v-bind="itemProps" :title="null">
                        <v-list-item-title class="text-truncate" data-cy="userSearchListItem">
                            {{
                                item.raw.practitionerResource?.fullName ||
                                (item.raw.practitionerResource?.firstName && item.raw.practitionerResource?.lastName
                                    ? `${item.raw.practitionerResource.firstName} ${item.raw.practitionerResource.lastName}`
                                    : 'Unknown Provider')
                            }}
                        </v-list-item-title>
                    </v-list-item>
                </template>
                <template #selection="{ item }"> {{ itemTitle(item.raw) }}{{ item.raw.active === false ? ' (Inactive)' : '' }} </template>
            </v-autocomplete>
        </template>
    </DataProvider>
</template>
